import {
  useQueryAnalyticsAudioSummary,
  useQueryAnalyticsBreakdown,
  useQueryAnalyticsFeedback,
  useQueryAnalyticsSummary,
  useQueryAnalyticsTokenUsage,
  useQueryAnalyticsTop,
  useQueryAnalyticsTranslationSummary,
} from "@apis:analytics/analytics";
import { alpha, useTheme } from "@mui/material";
import { DonutSlice } from "@pages:app/analytics/components/cards/DonutCard";
import { summarySelector } from "@pages:app/analytics/selectors";
import {
  formatCompact,
  formatDuration,
  formatDurationCompact,
  generateActiveUsersChart,
  generateDurationChart,
  generateRequestsChart,
  generateTokenUsageChart,
  getClientTimezone,
  whatTimeUnit,
} from "@pages:app/analytics/utils";
import { useTranslation } from "react-i18next";

import {
  AnalyticsBreakdownItem,
  AnalyticsTopDocument,
  AnalyticsTopLanguagePair,
  AnalyticsTopUserRequests,
  RequestType,
  TimeUnit,
} from "@:openapi";

export type AnalyticsScopeParams = {
  botId?: string;
  organizationId?: string;
};

export type AnalyticsQueryKey =
  | "summary"
  | "topUsers"
  | "tokenUsage"
  | "feedback"
  | "topDocuments"
  | "translationSummary"
  | "languagePairs"
  | "requestsByFeature"
  | "docsSubtype"
  | "docsOperation"
  | "translationFileType"
  | "audioSummary"
  | "audioLanguage"
  | "audioLanguageDuration";

type UseAnalyticsDataParams = {
  requestType: RequestType;
  scopeParams: AnalyticsScopeParams;
  fromDate: string;
  toDate: string;
  rangeStart: string;
  rangeEnd: string;
  enabled: boolean;
  queries: AnalyticsQueryKey[];
};

const TOP_DONUT_SLICES = 5;
const OTHER_COLOR = "#9CA3AF";

export const useAnalyticsData = ({
  requestType,
  scopeParams,
  fromDate,
  toDate,
  rangeStart,
  rangeEnd,
  enabled,
  queries,
}: UseAnalyticsDataParams) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const granularity = whatTimeUnit(rangeStart, rangeEnd) as TimeUnit;
  const tz = getClientTimezone();

  // Only fetch what the active tab renders
  const needed = new Set(queries);
  const isOn = (key: AnalyticsQueryKey) => enabled && needed.has(key);

  const summaryQuery = useQueryAnalyticsSummary(
    { ...scopeParams, fromDate, toDate, requestType, granularity, tz },
    { enabled: isOn("summary"), throwOnError: false, select: summarySelector },
  );

  const topUsersQuery = useQueryAnalyticsTop(
    {
      ...scopeParams,
      fromDate,
      toDate,
      topType: "userRequests",
      requestType,
      limit: 0,
    },
    { enabled: isOn("topUsers"), throwOnError: false },
  );

  const tokenUsageQuery = useQueryAnalyticsTokenUsage(
    { ...scopeParams, fromDate, toDate, requestType, tz },
    { enabled: isOn("tokenUsage"), throwOnError: false },
  );

  const feedbackQuery = useQueryAnalyticsFeedback(
    { ...scopeParams, fromDate, toDate },
    { enabled: isOn("feedback"), throwOnError: false },
  );

  const topDocumentsQuery = useQueryAnalyticsTop(
    { ...scopeParams, fromDate, toDate, topType: "documents", limit: 0 },
    { enabled: isOn("topDocuments"), throwOnError: false },
  );

  const translationSummaryQuery = useQueryAnalyticsTranslationSummary(
    { ...scopeParams, fromDate, toDate },
    { enabled: isOn("translationSummary"), throwOnError: false },
  );

  const languagePairsQuery = useQueryAnalyticsTop(
    { ...scopeParams, fromDate, toDate, topType: "languagePairs", limit: 0 },
    { enabled: isOn("languagePairs"), throwOnError: false },
  );

  const requestsByFeatureQuery = useQueryAnalyticsBreakdown(
    { ...scopeParams, fromDate, toDate, dimension: "feature" },
    { enabled: isOn("requestsByFeature"), throwOnError: false },
  );

  const docsSubtypeQuery = useQueryAnalyticsBreakdown(
    { ...scopeParams, fromDate, toDate, dimension: "docsSubtype" },
    { enabled: isOn("docsSubtype"), throwOnError: false },
  );

  const docsOperationQuery = useQueryAnalyticsBreakdown(
    { ...scopeParams, fromDate, toDate, dimension: "docsOperation" },
    { enabled: isOn("docsOperation"), throwOnError: false },
  );

  const translationFileTypeQuery = useQueryAnalyticsBreakdown(
    { ...scopeParams, fromDate, toDate, dimension: "translationFileType" },
    { enabled: isOn("translationFileType"), throwOnError: false },
  );

  const audioSummaryQuery = useQueryAnalyticsAudioSummary(
    { ...scopeParams, fromDate, toDate, tz },
    { enabled: isOn("audioSummary"), throwOnError: false },
  );

  const audioLanguageQuery = useQueryAnalyticsBreakdown(
    { ...scopeParams, fromDate, toDate, dimension: "audioLanguage" },
    { enabled: isOn("audioLanguage"), throwOnError: false },
  );

  const audioLanguageDurationQuery = useQueryAnalyticsBreakdown(
    { ...scopeParams, fromDate, toDate, dimension: "audioLanguageDuration" },
    { enabled: isOn("audioLanguageDuration"), throwOnError: false },
  );

  // Charts
  const activeUsersChart = generateActiveUsersChart(
    t,
    rangeStart,
    rangeEnd,
    summaryQuery.data?.activeUsersPeriodData,
    theme.palette.primary.main,
  );

  const requestsChart = generateRequestsChart(
    t,
    rangeStart,
    rangeEnd,
    summaryQuery.data?.requestsPeriodData,
    theme.palette.primary.main,
  );

  const tokenUsageChart = generateTokenUsageChart(
    t,
    rangeStart,
    rangeEnd,
    tokenUsageQuery.data?.perDay,
    theme.palette.primary.main,
    "#161447",
  );

  const transcribedChart = generateDurationChart(
    rangeStart,
    rangeEnd,
    audioSummaryQuery.data?.transcribedPeriod,
    theme.palette.primary.main,
    "transcribed",
    t("analytics.transcribed"),
  );

  const processingChart = generateDurationChart(
    rangeStart,
    rangeEnd,
    audioSummaryQuery.data?.processingPeriod,
    theme.palette.primary.main,
    "processing",
    t("analytics.processing"),
  );

  // Donut slices
  const palette = [1, 0.72, 0.5, 0.34, 0.22].map((opacity) =>
    alpha(theme.palette.primary.main, opacity),
  );
  const colorAt = (index: number) => palette[index % palette.length];

  // Keep the top `TOP_DONUT_SLICES` slices, put everything else under "Other"
  const withOther = (slices: Omit<DonutSlice, "color">[]): DonutSlice[] => {
    const sorted = [...slices].sort((a, b) => b.value - a.value);
    const top = sorted
      .slice(0, TOP_DONUT_SLICES)
      .map((slice, index) => ({ ...slice, color: colorAt(index) }));

    if (sorted.length <= TOP_DONUT_SLICES) {
      return top;
    }

    const otherValue = sorted
      .slice(TOP_DONUT_SLICES)
      .reduce((acc, slice) => acc + slice.value, 0);

    return [
      ...top,
      {
        id: "__other__",
        label: t("analytics.other"),
        value: otherValue,
        color: OTHER_COLOR,
      },
    ];
  };

  const toSlices = (
    items: AnalyticsBreakdownItem[] | undefined,
  ): DonutSlice[] =>
    withOther(
      (items ?? []).map((item) => ({
        id: item.label,
        label: item.label,
        value: item.value,
      })),
    );

  const topUsersSlices: DonutSlice[] = withOther(
    ((topUsersQuery.data as AnalyticsTopUserRequests[] | undefined) ?? []).map(
      (row) => ({
        id: row.userId,
        label: row.fullName,
        value: row.numRequests,
      }),
    ),
  );

  const languagePairSlices: DonutSlice[] = withOther(
    (
      (languagePairsQuery.data as AnalyticsTopLanguagePair[] | undefined) ?? []
    ).map((row) => ({
      id: `${row.fromLang}-${row.toLang}`,
      label: t("analytics.languagePair", {
        from: row.fromLang,
        to: row.toLang,
      }),
      value: row.numTranslations,
    })),
  );

  const documentSlices: DonutSlice[] = withOther(
    ((topDocumentsQuery.data as AnalyticsTopDocument[] | undefined) ?? []).map(
      (row) => ({
        id: row.document,
        label: row.document,
        value: row.numReferences,
      }),
    ),
  );

  const modelSlices: DonutSlice[] = withOther(
    (tokenUsageQuery.data?.byModel ?? []).map((row) => ({
      id: `${row.provider}/${row.model}`,
      label: row.model,
      value: row.inputTokens + row.outputTokens,
    })),
  );

  const featureSlices = toSlices(requestsByFeatureQuery.data);
  const docsSubtypeSlices = toSlices(docsSubtypeQuery.data);
  const docsOperationSlices = toSlices(docsOperationQuery.data);
  const fileTypeSlices = toSlices(translationFileTypeQuery.data);
  const audioLanguageSlices = toSlices(audioLanguageQuery.data);
  const audioLanguageDurationSlices = toSlices(audioLanguageDurationQuery.data);

  const documentsProcessed = docsSubtypeQuery.data
    ? (docsSubtypeQuery.data.find((item) => item.label === "Document")?.value ??
      0)
    : undefined;

  const queryByKey = {
    summary: summaryQuery,
    topUsers: topUsersQuery,
    tokenUsage: tokenUsageQuery,
    feedback: feedbackQuery,
    topDocuments: topDocumentsQuery,
    translationSummary: translationSummaryQuery,
    languagePairs: languagePairsQuery,
    requestsByFeature: requestsByFeatureQuery,
    docsSubtype: docsSubtypeQuery,
    docsOperation: docsOperationQuery,
    translationFileType: translationFileTypeQuery,
    audioSummary: audioSummaryQuery,
    audioLanguage: audioLanguageQuery,
    audioLanguageDuration: audioLanguageDurationQuery,
  };
  const activeQueries: {
    isError: boolean;
    isLoading: boolean;
    refetch: () => unknown;
  }[] = queries.map((key) => queryByKey[key]);

  return {
    // Queries (for loading/error states)
    summaryQuery,
    topUsersQuery,
    tokenUsageQuery,
    feedbackQuery,
    topDocumentsQuery,
    translationSummaryQuery,
    languagePairsQuery,
    requestsByFeatureQuery,
    docsSubtypeQuery,
    docsOperationQuery,
    translationFileTypeQuery,
    audioSummaryQuery,
    audioLanguageQuery,
    audioLanguageDurationQuery,
    activeQueries,

    // Charts
    activeUsersChart,
    requestsChart,
    tokenUsageChart,
    transcribedChart,
    processingChart,

    // Slices
    topUsersSlices,
    languagePairSlices,
    documentSlices,
    modelSlices,
    featureSlices,
    docsSubtypeSlices,
    docsOperationSlices,
    fileTypeSlices,
    audioLanguageSlices,
    audioLanguageDurationSlices,

    // Derived values
    documentsProcessed,

    // Helpers
    sumSlices: (slices: DonutSlice[]) =>
      slices.reduce((acc, slice) => acc + slice.value, 0),
    formatCompact,
    formatDuration,
    formatDurationCompact,
  };
};

export type AnalyticsData = ReturnType<typeof useAnalyticsData>;
